/**
 * Scout Studio — Plan działań: kroki z diagnozy/konkurencji + cele klienta.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import { ListChecks, Check, Trash2, Goal, Loader2, Circle } from 'lucide-react';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';

export default function ScoutPlan({ client, onNavigate }) {
  const [items, setItems] = useState(null);

  const load = () => {
    fetch(`${API}/scout/plan/${client.id}`)
      .then((r) => r.json())
      .then((d) => setItems(d?.ok ? d.items : []))
      .catch(() => setItems([]));
  };
  useEffect(() => { setItems(null); load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (id) => {
    setItems((list) => list.map((it) => (it.id === id ? { ...it, status: it.status === 'done' ? 'todo' : 'done' } : it)));
    try { await fetch(`${API}/scout/plan/toggle`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }); } catch { /* noop */ }
  };
  const remove = async (id) => {
    setItems((list) => list.filter((it) => it.id !== id));
    try { await fetch(`${API}/scout/plan/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }); } catch { /* noop */ }
  };

  const todo = (items || []).filter((i) => i.status !== 'done');
  const done = (items || []).filter((i) => i.status === 'done');

  return (
    <div className="space-y-6" data-testid="view-plan">
      <div className="mb-2">
        <h2 className="text-lg font-bold flex items-center gap-2"><ListChecks className="w-5 h-5 text-[#FF8A3D]" /> Plan działań</h2>
        <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Kroki z diagnozy i analiz — odhaczaj wykonane. Cel prowadzi, Scout pilnuje.</p>
      </div>

      {/* Cele */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className={`${panel} p-4`} data-testid="plan-goal-30d">
          <div className="text-[11px] text-[var(--sc-t45)] flex items-center gap-1.5 mb-1"><Goal className="w-3.5 h-3.5 text-[#FF8A3D]" /> Cel na 30 dni</div>
          {client.goal_30d ? <div className="text-sm font-bold">{client.goal_30d}</div>
            : <button onClick={() => onNavigate?.('firma')} className="text-sm text-orange-300 hover:underline" data-testid="plan-set-goal-btn">Ustal cel w profilu →</button>}
        </div>
        <div className={`${panel} p-4`} data-testid="plan-goal-6m">
          <div className="text-[11px] text-[var(--sc-t45)] flex items-center gap-1.5 mb-1"><Goal className="w-3.5 h-3.5 text-[#8B5CF6]" /> Kierunek na 6 miesięcy</div>
          {client.goal_6m ? <div className="text-sm font-bold">{client.goal_6m}</div>
            : <span className="text-sm text-[var(--sc-t40)]">Jeszcze nie ustalony</span>}
        </div>
      </div>

      {items === null && (
        <div className={`${panel} p-8 text-center`}><Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FF8A3D]" /></div>
      )}

      {items !== null && items.length === 0 && (
        <div className={`${panel} p-8 text-center`} data-testid="plan-empty">
          <ListChecks className="w-10 h-10 mx-auto text-[var(--sc-t15)] mb-3" />
          <p className="text-sm text-[var(--sc-t50)]">Plan jest jeszcze pusty. Kroki dodasz z <button onClick={() => onNavigate?.('diagnoza')} className="text-orange-300 hover:underline font-semibold">Diagnozy firmy</button> albo z analizy konkurencji.</p>
        </div>
      )}

      {todo.length > 0 && (
        <div className={`${panel} p-5`} data-testid="plan-todo">
          <div className="text-sm font-bold mb-3">Do zrobienia ({todo.length})</div>
          <div className="space-y-2">
            {todo.map((it) => <PlanRow key={it.id} it={it} onToggle={toggle} onRemove={remove} />)}
          </div>
        </div>
      )}

      {done.length > 0 && (
        <div className={`${panel} p-5`} data-testid="plan-done">
          <div className="text-sm font-bold mb-3 text-emerald-400">Zrobione ({done.length})</div>
          <div className="space-y-2">
            {done.map((it) => <PlanRow key={it.id} it={it} onToggle={toggle} onRemove={remove} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanRow({ it, onToggle, onRemove }) {
  const isDone = it.status === 'done';
  return (
    <div className="flex items-center gap-3 rounded-xl bg-[var(--sc-surface2)] border border-[var(--sc-border)] px-3.5 py-2.5" data-testid={`plan-item-${it.id}`}>
      <button onClick={() => onToggle(it.id)} data-testid={`plan-toggle-${it.id}`} aria-label="Oznacz krok"
        className={`shrink-0 transition ${isDone ? 'text-emerald-400' : 'text-[var(--sc-t30)] hover:text-orange-300'}`}>
        {isDone ? <Check className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${isDone ? 'line-through text-[var(--sc-t40)]' : 'text-[var(--sc-t85)]'}`}>{it.text}</div>
        {it.source && <div className="text-[10px] text-[var(--sc-t30)] mt-0.5">źródło: {it.source}</div>}
      </div>
      <button onClick={() => onRemove(it.id)} data-testid={`plan-delete-${it.id}`} aria-label="Usuń krok"
        className="shrink-0 text-[var(--sc-t30)] hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}
